import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = '';

// Custom Confirmation Dialog
const ConfirmDialog = ({ isOpen, title, message, userName, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="relative w-full max-w-sm">
        {/* Glow effect */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-red-500 to-[#DF1283] rounded-2xl opacity-60 blur-sm" />
        
        <div className="relative bg-black rounded-2xl border border-red-500/30 overflow-hidden">
          {/* Warning Icon */}
          <div className="flex justify-center pt-6">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 text-center">
            <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
            <p className="text-gray-400 mb-2">{message}</p>
            {userName && (
              <p className="text-[#DF1283] font-semibold text-lg mb-4">"{userName}"</p>
            )}
            <p className="text-gray-500 text-sm">This action cannot be undone.</p>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3 p-4 border-t border-red-500/20">
            <button
              onClick={onCancel}
              className="flex-1 py-3 px-4 rounded-lg font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 py-3 px-4 rounded-lg font-medium text-white bg-red-600 hover:bg-red-500 transition-all"
            >
              Delete User
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminPanel({ onClose }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, userId: null, userEmail: null });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const storedToken = localStorage.getItem('access_token');
      const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
      
      const response = await fetch(`${API_URL}/api/auth/admin/users`, {
        credentials: 'include',
        headers
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setUsers(data.users);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserAccess = async (userId, currentStatus) => {
    setUpdating(userId);
    setResetResult(null);
    try {
      const storedToken = localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
      
      const response = await fetch(`${API_URL}/api/auth/admin/users/${userId}/access`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ is_active: !currentStatus })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update user');
      }
      
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const resetUserPassword = async (userId, userEmail) => {
    setUpdating(userId);
    setResetResult(null);
    try {
      const storedToken = localStorage.getItem('access_token');
      const headers = {};
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
      
      const response = await fetch(`${API_URL}/api/auth/admin/users/${userId}/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers
      });
      
      if (!response.ok) {
        throw new Error('Failed to reset password');
      }
      
      const data = await response.json();
      setResetResult({
        email: data.user_email,
        password: data.temporary_password
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const openDeleteConfirm = (userId, userEmail) => {
    setDeleteConfirm({ isOpen: true, userId, userEmail });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({ isOpen: false, userId: null, userEmail: null });
  };

  const confirmDeleteUser = async () => {
    const { userId, userEmail } = deleteConfirm;
    closeDeleteConfirm();
    
    setUpdating(userId);
    setResetResult(null);
    try {
      const storedToken = localStorage.getItem('access_token');
      const headers = {};
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
      
      const response = await fetch(`${API_URL}/api/auth/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete user');
      }
      
      await fetchUsers();
      setSelectedUser(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // User Detail Modal
  const UserDetailModal = ({ u, onClose, onDelete, onResetPassword, onToggleAccess }) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="absolute -inset-0.5 bg-gradient-to-r from-[#DF1283] to-[#FF7901] rounded-2xl opacity-50 blur-sm" />
        
        <div className="relative bg-black/95 rounded-2xl border border-[#DF1283]/30 overflow-hidden">
          {/* Header with close */}
          <div className="flex items-center justify-between p-4 border-b border-[#4A0A66]/30">
            <h3 className="text-lg font-bold text-white">User Details</h3>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* User Info */}
          <div className="p-6">
            {/* Profile Photo & Name */}
            <div className="flex items-center gap-4 mb-6">
              {u.profile_photo ? (
                <img 
                  src={u.profile_photo} 
                  alt={u.first_name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-[#DF1283]"
                />
              ) : (
                <div className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold ${
                  u.role === 'admin'
                    ? 'bg-gradient-to-r from-[#D0FF0B] to-[#0FFFE2] text-black'
                    : 'bg-[#4A0A66] text-white'
                }`}>
                  {u.first_name?.[0]?.toUpperCase() || u.username?.[0]?.toUpperCase() || '?'}
                </div>
              )}
              <div>
                <h4 className="text-xl font-bold text-white">{u.first_name} {u.last_name}</h4>
                <p className="text-gray-400">@{u.username}</p>
                {u.role === 'admin' && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-gradient-to-r from-[#D0FF0B] to-[#0FFFE2] text-black text-xs font-bold rounded-full">
                    ADMIN
                  </span>
                )}
              </div>
            </div>

            {/* Details Grid */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#0a0014] rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Email</p>
                  <p className="text-white text-sm font-medium truncate">{u.email}</p>
                </div>
                <div className="bg-[#0a0014] rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Status</p>
                  <p className={`text-sm font-semibold ${u.is_active ? 'text-[#0FFFE2]' : 'text-[#DF1283]'}`}>
                    {u.is_active ? 'Active' : 'Deactivated'}
                  </p>
                </div>
              </div>

              <div className="bg-[#0a0014] rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Joined</p>
                <p className="text-white text-sm font-medium">{formatDate(u.created_at)}</p>
              </div>

              {/* Onboarding Data */}
              {u.onboarding_data && Object.keys(u.onboarding_data).length > 0 && (
                <div className="bg-[#0a0014] rounded-lg p-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Onboarding Choices</p>
                  <div className="space-y-2">
                    {u.onboarding_data.industry && (
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Industry</span>
                        <span className="text-[#0FFFE2] text-sm font-medium">{u.onboarding_data.industry}</span>
                      </div>
                    )}
                    {u.onboarding_data.usage_type && (
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Usage Type</span>
                        <span className="text-[#FF7901] text-sm font-medium capitalize">{u.onboarding_data.usage_type.replace('_', ' ')}</span>
                      </div>
                    )}
                    {u.onboarding_data.primary_goal && (
                      <div className="flex justify-between">
                        <span className="text-gray-400 text-sm">Primary Goal</span>
                        <span className="text-[#D0FF0B] text-sm font-medium">{u.onboarding_data.primary_goal}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Onboarding Status */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-400">Onboarding Completed</span>
                <span className={u.onboarding_completed ? 'text-[#0FFFE2]' : 'text-[#DF1283]'}>
                  {u.onboarding_completed ? 'Yes' : 'No'}
                </span>
              </div>
            </div>

            {/* Actions */}
            {u._id !== user._id && u.role !== 'admin' && (
              <div className="flex flex-col gap-3 mt-6 pt-4 border-t border-[#4A0A66]/30">
                <div className="flex gap-3">
                  <button
                    onClick={() => { onResetPassword(u._id, u.email); onClose(); }}
                    className="flex-1 px-4 py-2 rounded-lg font-medium text-sm bg-[#FF7901]/20 text-[#FF7901] hover:bg-[#FF7901]/30 transition-all"
                  >
                    Reset Password
                  </button>
                  <button
                    onClick={() => { onToggleAccess(u._id, u.is_active); onClose(); }}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      u.is_active
                        ? 'bg-[#DF1283]/20 text-[#DF1283] hover:bg-[#DF1283]/30'
                        : 'bg-[#0FFFE2]/20 text-[#0FFFE2] hover:bg-[#0FFFE2]/30'
                    }`}
                  >
                    {u.is_active ? 'Revoke Access' : 'Grant Access'}
                  </button>
                </div>
                <button
                  onClick={() => onDelete(u._id, u.email)}
                  className="w-full px-4 py-2 rounded-lg font-medium text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30 transition-all"
                >
                  Delete User Permanently
                </button>
              </div>
            )}
            {u._id !== user._id && u.role === 'admin' && (
              <div className="mt-6 pt-4 border-t border-[#4A0A66]/30">
                <p className="text-gray-500 text-sm text-center">Admin accounts cannot be modified</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-4xl max-h-[80vh] overflow-hidden">
        {/* Glow effect */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-[#4A0A66] via-[#DF1283] to-[#FF7901] rounded-2xl opacity-50 blur-sm" />
        
        <div className="relative bg-black/95 rounded-2xl border border-[#4A0A66]/30 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#4A0A66]/30">
            <div>
              <h2 className="text-2xl font-bold text-white">User Management</h2>
              <p className="text-gray-400 text-sm mt-1">Click on a user to see more details</p>
            </div>
            <button
              onClick={onClose}
              data-testid="close-admin-panel"
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {error && (
              <div className="bg-[#DF1283]/10 border border-[#DF1283]/50 rounded-lg px-4 py-3 text-[#DF1283] text-sm mb-4">
                {error}
              </div>
            )}

            {resetResult && (
              <div className="bg-[#0FFFE2]/10 border border-[#0FFFE2]/50 rounded-lg px-4 py-4 mb-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-[#0FFFE2] font-medium mb-2">Password Reset Successfully!</p>
                    <p className="text-gray-300 text-sm">Share these credentials with the user:</p>
                    <div className="mt-2 bg-black/50 rounded-lg p-3 font-mono text-sm">
                      <p className="text-gray-400">Email: <span className="text-white">{resetResult.email}</span></p>
                      <p className="text-gray-400">Temp Password: <span className="text-[#D0FF0B]">{resetResult.password}</span></p>
                    </div>
                  </div>
                  <button onClick={() => setResetResult(null)} className="text-gray-400 hover:text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin h-8 w-8 border-2 border-[#0FFFE2] border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div
                    key={u._id}
                    data-testid={`user-row-${u.username}`}
                    onClick={() => setSelectedUser(u)}
                    className={`p-4 rounded-xl border transition-all cursor-pointer hover:scale-[1.01] ${
                      u.is_active
                        ? 'bg-[#0a0014] border-[#4A0A66]/50 hover:border-[#DF1283]/50'
                        : 'bg-[#1a0020] border-[#DF1283]/30 hover:border-[#DF1283]/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Avatar - Show profile photo if available */}
                        {u.profile_photo ? (
                          <img 
                            src={u.profile_photo} 
                            alt={u.first_name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-[#4A0A66]"
                          />
                        ) : (
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                            u.role === 'admin'
                              ? 'bg-gradient-to-r from-[#D0FF0B] to-[#0FFFE2] text-black'
                              : 'bg-[#4A0A66] text-white'
                          }`}>
                            {u.first_name?.[0]?.toUpperCase() || u.username?.[0]?.toUpperCase() || '?'}
                          </div>
                        )}
                        
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">
                              {u.first_name} {u.last_name}
                            </span>
                            {u.role === 'admin' && (
                              <span className="px-2 py-0.5 bg-gradient-to-r from-[#D0FF0B] to-[#0FFFE2] text-black text-xs font-bold rounded-full">
                                ADMIN
                              </span>
                            )}
                            {!u.is_active && (
                              <span className="px-2 py-0.5 bg-[#DF1283]/20 text-[#DF1283] text-xs font-medium rounded-full">
                                DEACTIVATED
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-400">@{u.username} · {u.email}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Joined {formatDate(u.created_at)}
                            {u.onboarding_data?.industry && (
                              <span className="text-[#0FFFE2]"> · {u.onboarding_data.industry}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Click indicator */}
                      <div className="text-gray-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                ))}

                {users.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    No users found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-[#4A0A66]/30 flex justify-between items-center">
            <span className="text-sm text-gray-400">
              {users.length} user{users.length !== 1 ? 's' : ''} total
            </span>
            <button
              onClick={fetchUsers}
              className="text-sm text-[#0FFFE2] hover:text-[#D0FF0B] transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailModal 
          u={selectedUser} 
          onClose={() => setSelectedUser(null)}
          onDelete={openDeleteConfirm}
          onResetPassword={resetUserPassword}
          onToggleAccess={toggleUserAccess}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete User?"
        message="Are you sure you want to permanently delete this user?"
        userName={deleteConfirm.userEmail}
        onConfirm={confirmDeleteUser}
        onCancel={closeDeleteConfirm}
      />
    </div>
  );
}
